import { useEffect } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { API_BASE_URL } from './api/client'
import { canAdministerAccounts, hasCapability, requiresTwoFactor, useAuth } from './context/AuthContext'
import { TwoFactorEnrolment } from './components/TwoFactorEnrolment'
import { LoginPage } from './pages/LoginPage'
import { UsersPage } from './pages/UsersPage'
import { DashboardPage } from './pages/DashboardPage'
import { InsightsEmbedPage } from './pages/InsightsEmbedPage'
import { FourWallPage } from './pages/FourWallPage'
import { GridViewerPage } from './pages/GridViewerPage'
import { SalesAnalysisPage } from './pages/SalesAnalysisPage'
import { ProductOverviewPage } from './pages/ProductOverviewPage'
import { AccountSettingsPage } from './pages/AccountSettingsPage'
import { ProfileSettingsPage } from './pages/ProfileSettingsPage'
import { SettingsSubMenu } from './components/SettingsSubMenu'
import { MobileTabBar } from './components/MobileTabBar'
import { TopNav, type AppView } from './components/TopNav'
import { useDashboard, toDashboardView } from './hooks/useDashboard'
import { useTheme } from './hooks/useTheme'
import { MOCK_DASHBOARD } from './api/mockDashboard'
import { displayName, roleLabel } from './lib/labels'
import { isKnownPath, isPublicPath, navigate, useRoute } from './lib/router'
import { DASHBOARD_CATEGORY_PATHS } from './lib/dashboardRoutes'

const MOBIETRAIN_PATHS = new Set(['/insights/mobietrain-insights', '/insights/mobietrain'])
const FOUR_WALL_PATHS = new Set(['/insights/four-wall-analysis', '/insights/four-wall'])
const GRID_PATHS = new Set(['/insights/grid-viewer', '/insights/grid'])
const SALES_PATHS = new Set(['/insights/sales-analysis', '/insights/sales'])
const PRODUCT_PATHS = new Set(['/insights/product-overview', '/insights/products'])
const LEGACY_PATH_REDIRECTS: Record<string, string> = {
  '/insights/mobietrain': '/insights/mobietrain-insights',
  '/insights/four-wall': '/insights/four-wall-analysis',
  '/insights/grid': '/insights/grid-viewer',
  '/insights/sales': '/insights/sales-analysis',
  '/insights/products': '/insights/product-overview',
}

/** In-app screens whose route needs a capability, not just a known path. */
const ROUTE_PERMISSIONS: Record<string, string> = {
  '/insights': 'insights.view',
  '/suppliers': 'suppliers.view',
  '/modules': 'modules.view',
  '/tools': 'tools.view',
  '/insights/four-wall-analysis': 'insights.four_wall',
  '/insights/four-wall': 'insights.four_wall',
  '/insights/sales-analysis': 'insights.sales_analysis',
  '/insights/sales': 'insights.sales_analysis',
  '/insights/product-overview': 'insights.product_overview',
  '/insights/products': 'insights.product_overview',
  '/insights/grid-viewer': 'insights.grid_viewer',
  '/insights/grid': 'insights.grid_viewer',
  '/insights/mobietrain-insights': 'insights.mobietrain',
  '/insights/mobietrain': 'insights.mobietrain',
}

export default function App() {
  const { theme, toggleTheme } = useTheme()
  const { data, isError, isFetching, refetch } = useDashboard()
  const { user, loading, signOut, refresh } = useAuth()
  const route = useRoute()

  const dashboard = data ? toDashboardView(data) : MOCK_DASHBOARD
  const view: AppView =
    route.path === '/users'
      ? 'users'
      : route.path === '/account'
        ? 'account'
        : route.path === '/profile'
          ? 'profile'
          : MOBIETRAIN_PATHS.has(route.path)
            ? 'insights'
            : FOUR_WALL_PATHS.has(route.path)
              ? 'fourwall'
              : GRID_PATHS.has(route.path)
                ? 'grid'
                : SALES_PATHS.has(route.path)
                  ? 'sales'
                  : PRODUCT_PATHS.has(route.path)
                    ? 'products'
                    : 'dashboard'
  const showUsers = user ? canAdministerAccounts(user) : false
  const gated = requiresTwoFactor(user)

  // Route guards: keep the URL honest about what the person may actually see.
  useEffect(() => {
    if (loading) return
    if (!user) {
      if (!isPublicPath(route.path)) navigate('/login', { replace: true })
      return
    }
    if (gated) {
      if (route.path !== '/setup-2fa') navigate('/setup-2fa', { replace: true })
      return
    }
    if (route.path === '/') {
      navigate(DASHBOARD_CATEGORY_PATHS.overview, { replace: true })
      return
    }
    const canonical = LEGACY_PATH_REDIRECTS[route.path]
    if (canonical) {
      navigate(canonical, { replace: true })
      return
    }
    const requiredPermission = ROUTE_PERMISSIONS[route.path]
    if (requiredPermission && !hasCapability(user, requiredPermission)) {
      navigate(DASHBOARD_CATEGORY_PATHS.overview, { replace: true })
      return
    }
    if (route.path === '/users' && !showUsers) {
      navigate(DASHBOARD_CATEGORY_PATHS.overview, { replace: true })
      return
    }
    if (!isKnownPath(route.path) || route.path === '/login' || route.path === '/setup-2fa') {
      navigate(DASHBOARD_CATEGORY_PATHS.overview, { replace: true })
    }
  }, [loading, user, gated, showUsers, route.path])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-ink-950">
        <p className="text-xs text-slate-500 dark:text-slate-400">Loading Store OS…</p>
      </div>
    )
  }

  if (!user) {
    return <LoginPage />
  }

  // Manager-2FA policy (D-21): managers must pair an authenticator app first.
  if (gated) {
    return (
      // Pairing is tall — QR, setup key, code field, buttons — and on a phone the
      // keyboard makes it taller still. A centred flex box clips content above the
      // scroll origin, so start at the top on phones and centre only from `sm` (D-108).
      <div className="flex min-h-screen min-h-dvh items-start justify-center overflow-y-auto bg-slate-100 px-4 py-8 sm:items-center sm:py-10 dark:bg-ink-950">
        <TwoFactorEnrolment onDone={() => void refresh()} onSignOut={() => void signOut()} />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-100 dark:bg-ink-950">
      <TopNav
        theme={theme}
        onToggleTheme={toggleTheme}
        displayName={displayName(user)}
        avatar={user.avatar}
        roleLabel={roleLabel(user)}
        onSignOut={() => void signOut()}
        onOpenSettings={(target) => navigate(target === 'account' ? '/account' : '/profile')}
        onNavigate={navigate}
        showUserManagement={showUsers}
      />

      <main className="mx-auto flex w-full max-w-[1600px] flex-1 flex-col gap-4 px-3 pb-28 pt-4 sm:gap-5 sm:px-4 sm:py-6 lg:px-8 lg:pb-6">
        {isError && (
          <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-xs text-amber-800 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Live dashboard data is unavailable at <span className="font-mono">{API_BASE_URL}</span> —
              showing the Salsa Shop placeholder snapshot. Start the stack with{' '}
              <span className="font-mono">docker compose up --build</span>.
            </span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold hover:bg-amber-500/10"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
              Retry
            </button>
          </div>
        )}

        {(view === 'users' || view === 'account' || view === 'profile') && (
          <SettingsSubMenu
            activeKey={view}
            showUserManagement={showUsers}
            panelId="settings-panel"
          />
        )}

        <div
          id={
            view === 'users' || view === 'account' || view === 'profile'
              ? 'settings-panel'
              : undefined
          }
          className="min-w-0"
        >
          {view === 'users' ? (
            <UsersPage />
          ) : view === 'account' ? (
            <AccountSettingsPage />
          ) : view === 'profile' ? (
            <ProfileSettingsPage />
          ) : view === 'insights' ? (
            <InsightsEmbedPage />
          ) : view === 'fourwall' ? (
            <FourWallPage />
          ) : view === 'grid' ? (
            <GridViewerPage />
          ) : view === 'sales' ? (
            <SalesAnalysisPage />
          ) : view === 'products' ? (
            <ProductOverviewPage />
          ) : (
            <DashboardPage dashboard={dashboard} />
          )}
        </div>

        <footer className="mt-auto hidden flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-4 text-[11px] text-slate-400 sm:flex dark:border-white/10 dark:text-slate-500">
          <span>
            Salsa Shop · Store OS v0.1.0 — Restaurant Operations Platform
          </span>
          <span className="font-mono">
            API {API_BASE_URL} · docs /docs
          </span>
        </footer>
      </main>

      {/* Phones get a thumb-reachable bottom bar; from `lg` up the top bar and the
          dashboard tab bar already carry the same destinations (D-106). */}
      <MobileTabBar
        theme={theme}
        onToggleTheme={toggleTheme}
        displayName={displayName(user)}
        avatar={user.avatar}
        roleLabel={roleLabel(user)}
        onSignOut={() => void signOut()}
        onOpenSettings={(target) => navigate(target === 'account' ? '/account' : '/profile')}
        user={user}
      />
    </div>
  )
}
